import { useState } from 'react';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { useStripeFinancials } from '../../context/StripeFinancialsContext';
import { supabase } from '../../lib/supabase';
import { Badge, Button, Card, Icon, KebabMenu, Table } from '../ui';
import type { TableColumn } from '../ui';
import useMediaQuery, { PHONE_QUERY } from '../clients/useMediaQuery';

// Phones drop Source and Type: the amount's sign and colour already say revenue vs
// expense, and only manual rows get the actions menu.
const PHONE_HIDDEN = new Set(['isManual', 'type']);

interface TransactionRow extends Record<string, unknown> {
  id: string;
  date: string;
  description: string;
  client: string | null;
  isManual: boolean;
  amount: number;
  currency: string;
}

const formatCurrency = (amountInCents: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(Math.abs(amountInCents) / 100);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

export default function TransactionsList() {
  const { transactions, isLoading, refreshTransactions } = useStripeFinancials();
  const [isExpanded, setIsExpanded] = useState(false);
  const phone = useMediaQuery(PHONE_QUERY);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const displayedTransactions = isExpanded ? transactions : transactions.slice(0, 10);

  const handleDelete = async (transactionId: string) => {
    if (!confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) {
      return;
    }

    setDeletingId(transactionId);
    try {
      const { error } = await supabase
        .from('stripe_transactions')
        .delete()
        .eq('id', transactionId);

      if (error) throw error;

      await refreshTransactions();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert('Failed to delete transaction');
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading && transactions.length === 0) {
    return (
      <Card>
        <div
          style={{
            padding: 'var(--space-8) 0',
            textAlign: 'center',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-faint)',
          }}
        >
          Loading...
        </div>
      </Card>
    );
  }

  if (transactions.length === 0) {
    return (
      <Card>
        <div style={{ padding: 'var(--space-10) 0', textAlign: 'center' }}>
          <Icon name="inbox" size={28} style={{ color: 'var(--text-faint)', marginBottom: 'var(--space-3)' }} />
          <div
            style={{
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              color: 'var(--text-body)',
              marginBottom: 'var(--space-1)',
            }}
          >
            No Transactions Yet
          </div>
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
            Sync your Stripe data to see transactions here.
          </p>
        </div>
      </Card>
    );
  }

  const columns: TableColumn<TransactionRow>[] = [
    { key: 'date', label: 'Date', mono: true, render: (v: string) => formatDate(v) },
    {
      key: 'description',
      label: 'Description',
      render: (v: string, row) => (
        <span style={{ whiteSpace: 'normal' }}>
          <span style={{ display: 'block' }}>{v || 'No description'}</span>
          {row.client ? (
            <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              {row.client}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'isManual',
      label: 'Source',
      render: (v: boolean) => <Badge tone={v ? 'neutral' : 'info'}>{v ? 'Manual' : 'Stripe'}</Badge>,
    },
    {
      key: 'type',
      label: 'Type',
      render: (v: number) => <Badge tone={v >= 0 ? 'up' : 'idle'}>{v >= 0 ? 'Revenue' : 'Expense'}</Badge>,
    },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      mono: true,
      render: (v: number, row) => (
        <span style={{ color: v < 0 ? 'var(--text-muted)' : 'var(--status-up)' }}>
          {v < 0 ? '-' : '+'}
          {formatCurrency(v, row.currency)}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (_: unknown, row) =>
        row.isManual ? (
          <KebabMenu
            label="Transaction actions"
            actions={[
              {
                label: deletingId === row.id ? 'Deleting...' : 'Delete',
                icon: faTrash,
                danger: true,
                onClick: () => handleDelete(row.id),
              },
            ]}
          />
        ) : null,
    },
  ];

  const rows: TransactionRow[] = displayedTransactions.map((t) => ({
    id: t.id,
    date: t.created_at,
    description: t.description,
    client: t.customer_name,
    isManual: t.is_manual,
    type: t.amount,
    amount: t.amount,
    currency: t.currency,
  }));

  return (
    <div>
      <Table
        columns={phone ? columns.filter((c) => !PHONE_HIDDEN.has(c.key)) : columns}
        rows={rows}
        style={{ overflowX: 'auto' }}
      />

      {transactions.length > 10 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--space-3)' }}>
          <Button size="sm" variant="ghost" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? 'Show Less' : `Show All (${transactions.length - 10} more)`}
          </Button>
        </div>
      )}
    </div>
  );
}
